import { useQuery } from "@tanstack/react-query";
import { z } from "zod";

import { NIKE_URL } from "@/data/constants/url";
import { useLang } from "@/hooks/use-lang";
import { Lang } from "@/i18n-config";

interface HoldingsInfoParams {
  investor: string;
  category: HoldingsInfoCategory;
  page: number;
  pageSize: number;
  lang: Lang;
  sortBy?: string;
}

export type HoldingsInfoCategory =
  | "all"
  | "open-position"
  | "overweight"
  | "flat"
  | "underweight"
  | "close-position";

export const holdingsInfoDataSchema = z
  .object({
    symbol: z.string(),
    company: z.string(),
    sectorCategory: z.string(),
    weightPct: z.number(),
    amount: z.number(),
    units: z.number(),
    unitsPct: z.union([
      z.number(),
      z.enum(["清倉", "Sold Out", "建倉", "New Buy"]),
    ]),
  })
  .partial();

export const holdingsInfoSchema = z
  .object({
    page: z.number(),
    total: z.number(),
    pageSize: z.number(),
    data: z.array(holdingsInfoDataSchema),
    message: z.string(),
  })
  .partial();

export type HoldingsInfoData = z.infer<typeof holdingsInfoDataSchema>;
export type HoldingsInfo = z.infer<typeof holdingsInfoSchema>;

export const getInvestorHoldingsInfo = async ({
  investor,
  category,
  page,
  pageSize,
  lang,
  sortBy,
}: HoldingsInfoParams): Promise<HoldingsInfo> => {
  const queries = sortBy ? `?sortBy=${sortBy}` : "";
  const endpoint = `/13f/${investor}/holdings-info/${category}/${page}/${pageSize}`;
  const res = await fetch(`${NIKE_URL}${endpoint}${queries}`, {
    method: "GET",
    headers: { "Accept-Language": lang },
  });

  if (!res.ok) throw new Error(`API failed (${endpoint})`);
  return holdingsInfoSchema.parse(await res.json());
};

export const useInvestorHoldingsInfo = ({
  investor,
  category,
  page,
  pageSize,
  sortBy,
  initialData,
}: {
  investor: string;
  category: HoldingsInfoCategory;
  page: number;
  pageSize: number;
  sortBy?: string;
  initialData?: HoldingsInfo;
}) => {
  const { lang } = useLang();
  return useQuery(
    [
      "investor-holdings-info",
      investor,
      category,
      page,
      pageSize,
      sortBy,
      lang,
    ],
    () =>
      getInvestorHoldingsInfo({
        investor,
        category,
        page,
        pageSize,
        sortBy,
        lang,
      }),
    {
      enabled: !!investor && !!category && !!page && !!pageSize,
      keepPreviousData: true,
      initialData,
    }
  );
};
